import React from 'react';
import AuthService from '../services/AuthService';
import { AuthContext, AuthCredentials } from '../contexts/AuthContext';
import { authApi, onboardingApi, setOnUnauthorized } from '../api';

enum ActionTypes {
  RESTORE_TOKEN = 'RESTORE_TOKEN',
  SIGN_IN = 'SIGN_IN',
  SIGN_OUT = 'SIGN_OUT',
  SET_ONBOARDED = 'SET_ONBOARDED',
}

type Action = {
  type: ActionTypes;
  payload?: boolean;
};

export type AuthState = {
  isLoading: boolean;
  isSignout: boolean;
  isOnboarded: boolean;
};

type State = AuthState;

const reducer = (prevState: State, action: Action): State => {
  switch (action.type) {
    case ActionTypes.RESTORE_TOKEN:
      return {
        ...prevState,
        isSignout: action.payload ?? false,
        isLoading: false,
      };
    case ActionTypes.SIGN_IN:
      return {
        ...prevState,
        isSignout: false,
      };
    case ActionTypes.SIGN_OUT:
      return {
        ...prevState,
        isSignout: true,
        isOnboarded: false,
      };
    case ActionTypes.SET_ONBOARDED:
      return {
        ...prevState,
        isOnboarded: action.payload ?? false,
      };
    default:
      return prevState;
  }
};

const AuthProvider = ({ children }: { children: (data: State) => React.ReactNode }) => {
  const [state, dispatch] = React.useReducer(reducer, {
    isLoading: true,
    isSignout: false,
    isOnboarded: false,
  });

  const checkOnboardingStatus = React.useCallback(async () => {
    try {
      const status = await onboardingApi.getStatus();
      dispatch({ type: ActionTypes.SET_ONBOARDED, payload: status.isOnboarded });
    } catch {
      // If we can't check onboarding, assume not onboarded
      dispatch({ type: ActionTypes.SET_ONBOARDED, payload: false });
    }
  }, []);

  const authContext = React.useMemo(
    () => ({
      signIn: async (credentials: AuthCredentials) => {
        const response = await authApi.login(credentials);
        await AuthService.shared.setCredentials(response.accessToken);
        dispatch({ type: ActionTypes.SIGN_IN });
        // Check onboarding status after sign in
        await checkOnboardingStatus();
      },
      signOut: async () => {
        try {
          await authApi.logout();
        } catch {
          // Ignore logout API errors - we still want to clear local state
        }
        await AuthService.shared.removeCredentials();
        dispatch({ type: ActionTypes.SIGN_OUT });
      },
      signUp: async (credentials: AuthCredentials) => {
        const response = await authApi.register(credentials);
        await AuthService.shared.setCredentials(response.accessToken);
        dispatch({ type: ActionTypes.SIGN_IN });
        // New users are never onboarded
        dispatch({ type: ActionTypes.SET_ONBOARDED, payload: false });
      },
      refreshOnboardingStatus: checkOnboardingStatus,
    }),
    [checkOnboardingStatus],
  );

  React.useEffect(() => {
    // Set up unauthorized callback for axios interceptor
    setOnUnauthorized(() => {
      dispatch({ type: ActionTypes.SIGN_OUT });
    });

    const restoreToken = async () => {
      let userToken;

      try {
        userToken = await AuthService.shared.getCredentials();
      } catch {
        // Handle error
      }

      dispatch({ type: ActionTypes.RESTORE_TOKEN, payload: !userToken });

      // If user has token, check onboarding status
      if (userToken) {
        try {
          const status = await onboardingApi.getStatus();
          dispatch({ type: ActionTypes.SET_ONBOARDED, payload: status.isOnboarded });
        } catch {
          dispatch({ type: ActionTypes.SET_ONBOARDED, payload: false });
        }
      }
    };

    restoreToken();
  }, []);

  return <AuthContext.Provider value={authContext}>{children(state)}</AuthContext.Provider>;
};

export default AuthProvider;
